import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { SITE_CONFIG } from '@vastra/constants';
import { api } from '../services/api';
import type { SiteSettings } from '@vastra/types';

interface FooterProps {
  categories?: string[];
}

const Footer: React.FC<FooterProps> = ({ categories = [] }) => {
  const currentYear = new Date().getFullYear();
  const [site, setSite] = useState<SiteSettings | null>(null);

  useEffect(() => {
    let cancelled = false;
    api
      .getSiteSettings()
      .then((s) => {
        if (!cancelled) setSite(s);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  const businessName = site?.businessName ?? SITE_CONFIG.siteName;
  const contactPhone = site?.contactPhone ?? SITE_CONFIG.contactPhone;
  const contactEmail = site?.contactEmail ?? SITE_CONFIG.contactEmail;
  const contactWhatsApp = site?.contactWhatsApp ?? SITE_CONFIG.contactWhatsApp;
  const instagram = site?.socialMedia?.instagram ?? SITE_CONFIG.socialLinks?.instagram;
  const youtube = site?.socialMedia?.youtube ?? SITE_CONFIG.socialLinks?.youtube;
  const facebook = site?.socialMedia?.facebook ?? SITE_CONFIG.socialLinks?.facebook;
  const twitter = site?.socialMedia?.twitter;

  return (
    <footer className="bg-[#0f1a22] text-[#c5d0cc]" role="contentinfo">
      <div className="border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-14 sm:py-16 text-center">
          <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-[#1f6f66] mb-3">Stay connected</p>
          <h2 className="font-serif text-3xl font-medium text-[#ffffff] mb-4 tracking-tight">Join the circle</h2>
          <p className="text-sm text-[#9aa8a4] max-w-md mx-auto mb-8 leading-relaxed">
            New arrivals, drape stories, and occasion ideas — follow along on social.
          </p>
          <div className="flex items-center justify-center gap-6">
            {instagram && (
              <a href={instagram} target="_blank" rel="noopener noreferrer" className="text-[#9aa8a4] hover:text-[#c5ddd9] transition-colors" aria-label="Follow us on Instagram">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z" /></svg>
              </a>
            )}
            {youtube && (
              <a href={youtube} target="_blank" rel="noopener noreferrer" className="text-[#9aa8a4] hover:text-[#c5ddd9] transition-colors" aria-label="Subscribe on YouTube">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true"><path d="M23.498 6.186a3.016 3.016 0 00-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 00.502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 002.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 002.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" /></svg>
              </a>
            )}
            {facebook && (
              <a href={facebook} target="_blank" rel="noopener noreferrer" className="text-[#9aa8a4] hover:text-[#c5ddd9] transition-colors" aria-label="Follow us on Facebook">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" /></svg>
              </a>
            )}
            {twitter && (
              <a href={twitter} target="_blank" rel="noopener noreferrer" className="text-[#9aa8a4] hover:text-[#c5ddd9] transition-colors" aria-label="Follow us on Twitter/X">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" /></svg>
              </a>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10 lg:gap-12">
        <div>
          <h3 className="notranslate text-xl font-medium serif text-[#ffffff] mb-2">{businessName}</h3>
          <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-[#1f6f66] mb-5">{SITE_CONFIG.tagline}</p>
          <p className="text-sm text-[#9aa8a4] leading-relaxed">
            Celebrating India&apos;s textile traditions through curated sarees — with stories as varied as the fabrics themselves.
          </p>
        </div>

        <div>
          <h4 className="text-[11px] font-semibold uppercase tracking-[0.2em] text-[#7a8a86] mb-5">Collections</h4>
          <ul className="space-y-3" role="list">
            {categories.slice(0, 6).map((cat) => (
              <li key={cat}>
                <Link to={`/products?category=${encodeURIComponent(cat)}`} className="text-sm text-[#c5d0cc] hover:text-[#c5ddd9] transition-colors">
                  {cat}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        <div>
          <h4 className="text-[11px] font-semibold uppercase tracking-[0.2em] text-[#7a8a86] mb-5">Information</h4>
          <ul className="space-y-3" role="list">
            <li><Link to="/order-status" className="text-sm text-[#c5d0cc] hover:text-[#c5ddd9] transition-colors">Track order</Link></li>
            <li><Link to="/shipping" className="text-sm text-[#c5d0cc] hover:text-[#c5ddd9] transition-colors">Shipping & Returns</Link></li>
            <li><Link to="/terms" className="text-sm text-[#c5d0cc] hover:text-[#c5ddd9] transition-colors">Terms & Conditions</Link></li>
            <li><Link to="/privacy" className="text-sm text-[#c5d0cc] hover:text-[#c5ddd9] transition-colors">Privacy Policy</Link></li>
            <li><Link to="/blog" className="text-sm text-[#c5d0cc] hover:text-[#c5ddd9] transition-colors">Journal</Link></li>
            <li><Link to="/sitemap" className="text-sm text-[#c5d0cc] hover:text-[#c5ddd9] transition-colors">Sitemap</Link></li>
          </ul>
        </div>

        <div>
          <h4 className="text-[11px] font-semibold uppercase tracking-[0.2em] text-[#7a8a86] mb-5">Get in touch</h4>
          <ul className="space-y-4" role="list">
            {contactPhone && (
              <li>
                <a href={`tel:${contactPhone.replace(/\s/g, '')}`} className="text-sm text-[#c5d0cc] hover:text-[#c5ddd9] transition-colors">
                  {contactPhone}
                </a>
              </li>
            )}
            {contactWhatsApp && (
              <li>
                <a href={`https://wa.me/${contactWhatsApp.replace(/\D/g, '')}`} target="_blank" rel="noopener noreferrer" className="text-sm text-[#c5d0cc] hover:text-[#c5ddd9] transition-colors">
                  WhatsApp
                </a>
              </li>
            )}
            {contactEmail && (
              <li>
                <a href={`mailto:${contactEmail}`} className="text-sm text-[#c5d0cc] hover:text-[#c5ddd9] transition-colors break-all">
                  {contactEmail}
                </a>
              </li>
            )}
          </ul>
        </div>
      </div>

      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-5 flex flex-col md:flex-row items-center justify-between gap-3">
          <p className="text-[10px] font-semibold uppercase tracking-widest text-[#7a8a86]">
            © {currentYear} <span className="notranslate">{businessName}</span>
          </p>
          <p className="text-[10px] text-[#5a6a66]">
            Maintained by{' '}
            <a
              href="https://www.vaagatech.com"
              target="_blank"
              rel="noopener noreferrer"
              className="notranslate text-[#7a8a86] hover:text-[#c5ddd9] transition-colors underline-offset-2 hover:underline"
            >
              VaagaTech
            </a>
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
